import { useRingGroups, useQueues } from '../../lib/api/hooks-tms.js';
import { useDevices } from '../../lib/api/hooks-extension.js';
import { useRealtime } from '../../lib/api/realtime.js';
import { Empty, Badge } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { relativeTime } from '../../lib/format.js';
import type { Extension } from '@arcnet/contracts';

/**
 * Where this extension is used.
 *
 * Read-only on purpose: membership is a property of the hunt list or queue, and
 * editing it from both ends produces two screens that can disagree about the
 * same row. Links point at the owning object instead.
 */
export function MembershipTab({ tenantId, ext }: { tenantId: string; ext: Extension }) {
  const groups = useRingGroups(tenantId);
  const queues = useQueues(tenantId);

  const inGroups = (groups.data?.data ?? []).filter(
    (g) => g.members.some((m) => m.extension_id === ext.id));
  const inQueues = (queues.data?.data ?? []).filter(
    (q) => q.agents.some((a) => a.extension_id === ext.id));

  const nothing = inGroups.length === 0 && inQueues.length === 0;

  return (
    <>
      <div className="arc-banner arc-banner-info" style={{ marginBottom: 14 }}>
        <Icon name="warning" size={14} />
        <span>
          Membership is edited on the hunt list or queue itself, so there is one
          place that decides who is in it.
        </span>
      </div>

      {nothing ? (
        <div className="arc-card">
          <Empty icon="users" title="Not in any hunt list or queue"
            hint="This extension only receives calls dialled directly or routed to it by name." />
        </div>
      ) : (
        <div className="arc-card">
          <div className="arc-table-wrap">
            <table className="arc-table">
              <thead>
                <tr><th>Type</th><th>Name</th><th>Dial code</th><th>Detail</th></tr>
              </thead>
              <tbody>
                {inGroups.map((g) => (
                  <tr key={g.id}>
                    <td><Badge tone="muted">hunt list</Badge></td>
                    <td style={{ fontWeight: 550 }}>{g.name}</td>
                    <td className="arc-mono">{g.extension ?? '—'}</td>
                    <td className="arc-muted">
                      {g.strategy.replace(/_/g, ' ')} · {g.members.length} members
                    </td>
                  </tr>
                ))}
                {inQueues.map((q) => {
                  const agent = q.agents.find((a) => a.extension_id === ext.id);
                  return (
                    <tr key={q.id}>
                      <td><Badge tone="info">queue</Badge></td>
                      <td style={{ fontWeight: 550 }}>{q.name}</td>
                      <td className="arc-mono">{q.extension ?? '—'}</td>
                      <td className="arc-muted">
                        tier {agent?.tier_level ?? 1} · {q.agents.length} agents
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

/**
 * Live SIP state for this extension.
 *
 * The first question in any "my phone is not working" call is whether the
 * handset is registered at all, so it is one click from the extension rather
 * than behind a diagnostics menu.
 */
/**
 * How current the answer is.
 *
 * Registration is ephemeral state cached from a node push. A "registered" badge
 * from a ten-minute-old snapshot is a lie, and staleness is precisely the
 * question when someone says their phone stopped working.
 */
function SnapshotAge({ at }: { at: string }) {
  const ageSec = Math.round((Date.now() - new Date(at).getTime()) / 1000);
  const stale = ageSec > 120;
  return (
    <span className={stale ? 'arc-badge arc-badge-warn' : 'arc-subtle'}
      style={stale ? undefined : { fontSize: 11.5 }}>
      {stale && <Icon name="warning" size={10} />}
      {ageSec < 60 ? `updated ${ageSec}s ago` : `updated ${Math.round(ageSec / 60)}m ago`}
    </span>
  );
}

export function RegistrationTab({ tenantId, ext }: { tenantId: string; ext: Extension }) {
  // Opens the event stream for as long as this tab is mounted. On a register or
  // unregister the row refetches in about a second instead of on the next sweep.
  const live = useRealtime(tenantId);
  const devices = useDevices(tenantId);
  const assigned = (devices.data?.data ?? []).filter(
    (d) => d.lines.some((l) => l.extension_id === ext.id));

  const reg = ext.registration;

  return (
    <>
      <div className="arc-card" style={{ marginBottom: 14 }}>
        <div className="arc-card-head">
          <h2 className="arc-h2">SIP registration</h2>
          {/* Nothing when the stream is healthy. A "live" pill is only ever
              news when it is absent, so the staleness marker below carries the
              whole signal on its own. */}
          {live !== 'live' && reg && <SnapshotAge at={reg.snapshot_at} />}
        </div>
        <div className="arc-card-body">
          {reg === undefined ? (
            <div className="arc-banner arc-banner-info">
              <Icon name="warning" size={14} />
              <div>
                <div style={{ fontWeight: 600 }}>No node has reported on this extension</div>
                <div style={{ fontSize: 12 }}>
                  Different from &ldquo;not registered&rdquo;: the switch may not be
                  enrolled with the control plane, or has not pushed a snapshot yet.
                </div>
              </div>
            </div>
          ) : (
            <>
              {!reg.registered && (
                <div className="arc-banner arc-banner-warn" style={{ marginBottom: 12 }}>
                  <Icon name="warning" size={14} />
                  <span>
                    Offline{reg.offline_since ? ` since ${new Date(reg.offline_since)
                      .toLocaleString('en-ZA', {
                        day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
                      })} (${relativeTime(reg.offline_since)})` : ''} — the handset
                    cannot receive calls. Everything below is what it last reported.
                  </span>
                </div>
              )}
              {/* Registered and unreachable is a DIFFERENT fault from not
                  registered, and the two are indistinguishable to the user:
                  the phone shows a line key either way. It usually means the
                  handset moved behind NAT that stopped forwarding, so the
                  registration stands but the switch cannot reach it. */}
              {reg.registered && /unreach|fail/i.test(reg.ping_status ?? '') && (
                <div className="arc-banner arc-banner-danger" style={{ marginBottom: 12 }}>
                  <Icon name="warning" size={14} />
                  <span>
                    Registered but not answering keep-alives. Calls to this extension
                    will ring nowhere until it re-registers.
                  </span>
                </div>
              )}
              <dl className="arc-kv">
                <dt>Status</dt>
                <dd>
                  <Badge tone={reg.registered ? 'ok' : 'danger'}>
                    <span className="arc-dot" aria-hidden="true" />
                    {reg.registered ? 'registered' : 'offline'}
                  </Badge>
                </dd>
                <dt>Last seen</dt>
                <dd>
                  {reg.last_seen_at
                    ? <>
                        {relativeTime(reg.last_seen_at)}
                        <span className="arc-subtle" style={{ marginLeft: 8 }}>
                          {new Date(reg.last_seen_at).toLocaleString('en-ZA', {
                            day: '2-digit', month: 'short', hour: '2-digit',
                            minute: '2-digit', second: '2-digit',
                          })}
                        </span>
                      </>
                    : <span className="arc-subtle">never</span>}
                </dd>
                <dt>{reg.registered ? 'Handset address' : 'Last address'}</dt>
                <dd className="arc-mono">
                  {reg.contact_ip
                    ? `${reg.contact_ip}${reg.contact_port ? ':' + reg.contact_port : ''}`
                    : '—'}
                  {/* The device's own address, parsed from the Contact header.
                      The switch's network peer is the SIP proxy, so reporting
                      that would show every extension on one IP. */}
                  <span className="arc-subtle" style={{ marginLeft: 8 }}>
                    {reg.registered ? 'device' : 'last known'}
                  </span>
                </dd>
                <dt>Reachability</dt>
                <dd>
                  {reg.ping_status
                    ? <Badge tone={/unreach|fail/i.test(reg.ping_status) ? 'danger' : 'ok'}>
                        {reg.ping_status.toLowerCase()}
                      </Badge>
                    : <span className="arc-subtle">
                        {reg.registered ? 'not probed' : 'offline'}
                      </span>}
                </dd>
                <dt>Handset</dt>
                {/* The device's own User-Agent. It is what turns "my phone is
                    broken" into a firmware version and a model number. */}
                <dd>{reg.user_agent ?? <span className="arc-subtle">unknown</span>}</dd>
                <dt>Transport</dt>
                <dd>{reg.transport?.toUpperCase() ?? '—'}</dd>
                {reg.registered && (
                  <>
                    <dt>Registration expires</dt>
                    <dd>
                      {reg.expires_at
                        ? `${new Date(reg.expires_at).toLocaleTimeString('en-ZA')} (${relativeTime(reg.expires_at)})`
                        : '—'}
                    </dd>
                  </>
                )}
                {reg.first_seen_at && (
                  <>
                    <dt>First seen</dt>
                    <dd className="arc-muted">
                      {new Date(reg.first_seen_at).toLocaleDateString('en-ZA', {
                        day: '2-digit', month: 'short', year: 'numeric',
                      })}
                    </dd>
                  </>
                )}
                <dt>Contact URI</dt>
                <dd className="arc-mono arc-truncate" title={reg.contact ?? ''}>
                  {reg.contact ?? '—'}
                </dd>
              </dl>
            </>
          )}
        </div>
      </div>

      <div className="arc-card">
        <div className="arc-card-head">
          <h2 className="arc-h2">Handsets</h2>
          <span className="arc-subtle">{assigned.length} assigned</span>
        </div>
        {assigned.length === 0 ? (
          <Empty icon="phone" title="No handset assigned"
            hint="This extension is reachable only from a softphone or the browser until a device is linked." />
        ) : (
          <div className="arc-table-wrap">
            <table className="arc-table">
              <thead>
                <tr><th>Label</th><th>MAC</th><th>Vendor</th><th>Line</th><th>Status</th></tr>
              </thead>
              <tbody>
                {assigned.map((d) => {
                  const line = d.lines.find((l) => l.extension_id === ext.id);
                  return (
                    <tr key={d.id}>
                      <td style={{ fontWeight: 550 }}>{d.label ?? '—'}</td>
                      <td className="arc-mono">{d.mac_address ?? '—'}</td>
                      <td className="arc-muted">{d.vendor}{d.model ? ` ${d.model}` : ''}</td>
                      <td className="arc-num">{line?.line_number ?? '—'}</td>
                      <td>
                        <Badge tone={d.status === 'online' ? 'ok'
                          : d.status === 'provisioned' ? 'info' : 'muted'}>
                          <span className="arc-dot" aria-hidden="true" />{d.status}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
